import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Search, Send, MapPin, Users, StickyNote, History, Eye, RefreshCw, Bell, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingScreen, EmptyState } from "@/components/common";

const LEAD_STATUSES = ["New", "Under Review", "Unassigned", "Forwarded", "Viewed", "Interested", "Accepted", "Customer Contacted", "Converted", "Rejected", "Closed", "Lost"];
const STATUS_CLS = { New: "bg-sky-100 text-sky-700", "Under Review": "bg-amber-100 text-amber-700", Forwarded: "bg-brand-100 text-brand-600", Viewed: "bg-indigo-100 text-indigo-700", Interested: "bg-brand-100 text-brand-600", Accepted: "bg-emerald-100 text-emerald-700", "Customer Contacted": "bg-violet-100 text-violet-700", Converted: "bg-emerald-100 text-emerald-700", Rejected: "bg-red-100 text-red-700", Closed: "bg-navy-100 text-navy-500", Lost: "bg-red-100 text-red-700" };
const RESP_LABEL = { PENDING: "Sent", ACCEPTED: "Accepted", INTERESTED: "Interested", CONTACTED: "Contacted", NOT_AVAILABLE: "Not available", REJECTED: "Rejected" };

function ForwardDialog({ lead, onDone }) {
  const [open, setOpen] = useState(false);
  const [ags, setAgs] = useState([]);
  const [sel, setSel] = useState([]);
  const [share, setShare] = useState("basic");
  const [channels, setChannels] = useState({ inapp: true, email: false });
  const [f, setF] = useState({ city: "", service: "", q: "", verified: false });
  const [msg, setMsg] = useState("");
  const [confirm, setConfirm] = useState(false);

  const search = useCallback(async () => {
    const p = new URLSearchParams({ lead_id: lead.id });
    if (f.city) p.set("city", f.city);
    if (f.service) p.set("service", f.service);
    if (f.q) p.set("q", f.q);
    if (f.verified) p.set("verified", "true");
    const r = await api.get(`/admin/leadmgr/agencies?${p.toString()}`);
    setAgs(r.data);
  }, [lead.id, f]);
  useEffect(() => { if (open) { search(); setSel([]); } }, [open, search]);

  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const send = async () => {
    try {
      const chans = ["inapp", ...(channels.email ? ["email"] : [])];
      const r = await api.post(`/admin/leadmgr/leads/${lead.id}/forward`, { agency_ids: sel, share_mode: share, message: msg, channels: chans });
      toast.success(`Forwarded to ${r.data.forwarded.length} agenc${r.data.forwarded.length === 1 ? "y" : "ies"}${r.data.skipped_existing.length ? `, ${r.data.skipped_existing.length} already had it` : ""}`);
      setConfirm(false); setOpen(false); setMsg(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <Button className="bg-brand hover:bg-brand-600" onClick={() => setOpen(true)} data-testid="forward-lead-btn"><Send size={15} className="mr-1.5" /> Forward Lead</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="forward-dialog">
          <DialogHeader><DialogTitle>Forward {lead.lead_no} to agencies</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-4 gap-2">
            <Input placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="fwd-city" />
            <Input placeholder="Service" value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} data-testid="fwd-service" />
            <Input placeholder="Search name" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="fwd-q" />
            <label className="flex items-center gap-2 text-sm text-navy-700"><Checkbox checked={f.verified} onCheckedChange={(v) => setF({ ...f, verified: !!v })} data-testid="fwd-verified" /> Verified only</label>
          </div>
          <div className="max-h-64 overflow-y-auto border border-navy-200 rounded-lg divide-y divide-navy-100">
            {ags.length === 0 ? <p className="p-4 text-sm text-navy-500">No agencies match.</p> : ags.map((a) => (
              <label key={a.id} className={`flex items-center gap-3 p-3 text-sm ${a.already_forwarded ? "opacity-50" : "cursor-pointer hover:bg-navy-50"}`} data-testid={`fwd-agency-${a.id}`}>
                <Checkbox disabled={a.already_forwarded} checked={sel.includes(a.id)} onCheckedChange={() => toggle(a.id)} />
                <div className="flex-1">
                  <div className="flex items-center gap-2"><span className="font-medium text-navy-900">{a.name}</span>{a.verified && <ShieldCheck size={13} className="text-emerald-500" />}</div>
                  <span className="text-xs text-navy-500">{a.head_office?.city} · {a.total_guards} guards · {(a.services || []).slice(0, 2).join(", ")}</span>
                </div>
                {a.already_forwarded && <Badge className="bg-navy-100 text-navy-500 hover:bg-navy-100 text-xs">Already forwarded</Badge>}
              </label>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Contact sharing</Label>
              <Select value={share} onValueChange={setShare}>
                <SelectTrigger className="mt-1" data-testid="fwd-share"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic (hide phone)</SelectItem>
                  <SelectItem value="full">Full (share phone)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Message (optional)</Label><Input className="mt-1" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="fwd-message" /></div>
          </div>
          <div>
            <Label className="text-xs">Notify via</Label>
            <div className="flex gap-4 mt-1.5">
              <label className="flex items-center gap-2 text-sm text-navy-500"><Checkbox checked disabled data-testid="fwd-ch-inapp" /> In-app (always)</label>
              <label className="flex items-center gap-2 text-sm text-navy-700 cursor-pointer"><Checkbox checked={channels.email} onCheckedChange={(v) => setChannels({ ...channels, email: !!v })} data-testid="fwd-ch-email" /> Email</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-navy-300" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-600" disabled={sel.length === 0} onClick={() => setConfirm(true)} data-testid="fwd-send">Send Lead ({sel.length})</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="fwd-confirm">
          <DialogHeader><DialogTitle>Confirm forwarding</DialogTitle></DialogHeader>
          <p className="text-sm text-navy-700">You are about to share this customer requirement with {sel.length} selected agenc{sel.length === 1 ? "y" : "ies"} ({share} contact sharing, via in-app{channels.email ? " + email" : ""}). Continue?</p>
          <DialogFooter>
            <Button variant="outline" className="border-navy-300" onClick={() => setConfirm(false)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-600" onClick={send} data-testid="fwd-confirm-send">Confirm & Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function LeadDetail({ leadId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => api.get(`/admin/leadmgr/leads/${leadId}`).then((r) => setD(r.data)), [leadId]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => { await api.put(`/admin/leadmgr/leads/${leadId}/status`, { status }); toast.success("Status updated"); load(); onChanged(); };
  const addNote = async () => { if (!note.trim()) return; await api.post(`/admin/leadmgr/leads/${leadId}/notes`, { note }); setNote(""); toast.success("Note added"); load(); };
  const setShare = async (asnId, mode) => { await api.put(`/admin/leadmgr/assignments/${asnId}/share-mode`, { share_mode: mode }); toast.success("Sharing updated"); load(); };
  const remind = async (asnId) => { await api.post(`/admin/leadmgr/assignments/${asnId}/remind`); toast.success("Reminder sent"); load(); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail">
        {!d ? <LoadingScreen /> : (
          <>
            <DialogHeader><DialogTitle className="flex items-center gap-2">{d.lead_no}<span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLS[d.lead_status] || "bg-navy-100"}`}>{d.lead_status}</span></DialogTitle></DialogHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-navy-700"><span className="font-semibold">{d.security_requirement}</span> · {d.num_guards} guards · {d.location} {d.pincode}</div>
              <div className="flex items-center gap-2">
                <Select value={d.lead_status} onValueChange={setStatus}>
                  <SelectTrigger className="w-44 h-9" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <ForwardDialog lead={d} onDone={() => { load(); onChanged(); }} />
              </div>
            </div>

            <div className="rounded-lg bg-navy-50 border border-navy-100 p-3 text-sm grid sm:grid-cols-2 gap-x-6 gap-y-1">
              <div><span className="text-navy-400">Customer:</span> {d.customer_name}</div>
              <div><span className="text-navy-400">Mobile:</span> {d.mobile}</div>
              <div><span className="text-navy-400">Email:</span> {d.email || "—"}</div>
              <div><span className="text-navy-400">Property:</span> {d.property_type || "—"}</div>
              <div><span className="text-navy-400">Start:</span> {d.start_date || "—"}</div>
              <div><span className="text-navy-400">Source:</span> {d.lead_source || "Website"}</div>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-navy-900 text-sm mb-2">Forwarded to ({d.assignments.length})</h4>
              {d.assignments.length === 0 ? <p className="text-sm text-navy-500">Not forwarded yet.</p> : (
                <div className="space-y-2" data-testid="lead-assignments">
                  {d.assignments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between border border-navy-200 rounded-lg px-3 py-2 text-sm" data-testid={`assignment-${a.id}`}>
                      <div>
                        <span className="font-medium text-navy-900">{a.agency_name}</span>
                        <span className="ml-2 text-xs text-navy-500">{a.viewed ? "Viewed" : "Sent"} · {RESP_LABEL[a.response] || a.response} · {a.share_mode}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 border-navy-300 text-xs" onClick={() => setShare(a.id, a.share_mode === "full" ? "basic" : "full")} data-testid={`share-toggle-${a.id}`}>{a.share_mode === "full" ? "Hide phone" : "Share phone"}</Button>
                        <Button size="sm" variant="outline" className="h-7 border-navy-300 text-xs" onClick={() => remind(a.id)} data-testid={`remind-${a.id}`}><Bell size={12} /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 className="font-heading font-semibold text-navy-900 text-sm mb-2 flex items-center gap-1.5"><StickyNote size={15} /> Internal notes</h4>
              <div className="flex gap-2 mb-2">
                <Textarea rows={1} className="text-sm" placeholder="Add an internal note (agencies can't see this)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="lead-note-input" />
                <Button className="bg-navy-900 hover:bg-navy-800" onClick={addNote} data-testid="lead-note-add">Add</Button>
              </div>
              <div className="space-y-1.5">
                {d.notes.map((n) => <div key={n.id} className="text-sm text-navy-600 border-l-2 border-navy-200 pl-2"><span className="text-navy-800">{n.note}</span> <span className="text-xs text-navy-400">— {n.admin_name}, {n.created_at?.slice(0, 10)}</span></div>)}
              </div>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-navy-900 text-sm mb-2 flex items-center gap-1.5"><History size={15} /> Activity timeline</h4>
              <ol className="border-l-2 border-navy-200 pl-4 space-y-2" data-testid="lead-timeline">
                {d.activities.map((ac) => (
                  <li key={ac.id} className="text-sm relative">
                    <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-brand" />
                    <span className="text-navy-800">{ac.action}</span> <span className="text-xs text-navy-400">· {new Date(ac.timestamp).toLocaleString("en-IN")}</span>
                  </li>
                ))}
              </ol>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminLeadManagement() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [flt, setFlt] = useState({ status: "all", city: "", q: "" });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (flt.status !== "all") p.set("status", flt.status);
    if (flt.city) p.set("city", flt.city);
    if (flt.q) p.set("q", flt.q);
    api.get(`/admin/leadmgr/leads?${p.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/admin/leadmgr/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [flt]);
  useEffect(() => { load(); }, [load]);

  return (
    <DashboardShell menu={adminMenu} title="Lead Management">
      <PageHeader title="Lead Management" subtitle="Receive customer requirements, review and forward them to the right agencies." />
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[["Total Leads", stats.total], ["Unforwarded", stats.unforwarded], ["Forwarded", stats.by_status.Forwarded + stats.by_status.Viewed + stats.by_status.Interested + stats.by_status.Accepted], ["Converted", stats.by_status.Converted]].map(([l, v]) => (
            <div key={l} className="bg-white border border-navy-200 rounded-xl p-4"><div className="font-heading text-2xl font-bold text-navy-900">{v}</div><div className="brand-label text-navy-400 mt-1">{l}</div></div>
          ))}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Select value={flt.status} onValueChange={(v) => setFlt({ ...flt, status: v })}>
          <SelectTrigger className="w-48" data-testid="lm-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Input className="sm:w-40" placeholder="City" value={flt.city} onChange={(e) => setFlt({ ...flt, city: e.target.value })} data-testid="lm-city-filter" />
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" size={16} />
          <Input className="pl-9" placeholder="Search lead ID, customer, mobile, location…" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} data-testid="lm-search" />
        </div>
      </div>

      {!rows ? <LoadingScreen /> : rows.length === 0 ? (
        <EmptyState icon={ClipboardCheck} title="No leads found" hint="Customer requirements will appear here as they come in." />
      ) : (
        <div className="bg-white border border-navy-200 rounded-xl overflow-x-auto" data-testid="lm-list">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-navy-50 text-navy-500 text-xs uppercase"><tr>
              <th className="text-left p-3">Lead</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Requirement</th><th className="p-3">Status</th><th className="p-3">Forwarded</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-navy-100 hover:bg-navy-50/50" data-testid={`lm-row-${r.id}`}>
                  <td className="p-3"><span className="font-mono text-xs text-brand">{r.lead_no}</span><div className="text-xs text-navy-400">{r.created_at?.slice(0, 10)}</div></td>
                  <td className="p-3"><div className="font-medium text-navy-900">{r.customer_name}</div><div className="text-xs text-navy-500 flex items-center gap-1"><MapPin size={11} /> {r.location}</div></td>
                  <td className="p-3 text-navy-700"><div>{r.security_requirement}</div><div className="text-xs text-navy-500 flex items-center gap-1"><Users size={11} /> {r.num_guards} guards</div></td>
                  <td className="p-3 text-center"><span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLS[r.lead_status] || "bg-navy-100"}`}>{r.lead_status}</span></td>
                  <td className="p-3 text-center text-navy-600">{r.forwarded_count > 0 ? <span className="text-xs">{r.forwarded_count} sent · {r.viewed_count} viewed · {r.accepted_count} accepted</span> : <span className="text-xs text-navy-400">—</span>}</td>
                  <td className="p-3 text-right"><Button size="sm" className="bg-navy-900 hover:bg-navy-800" onClick={() => setOpenId(r.id)} data-testid={`lm-manage-${r.id}`}>Manage</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && <LeadDetail leadId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </DashboardShell>
  );
}
